import React, { useState, useRef, useEffect } from 'react';
import { Package, X, Save, ArrowRight, Trash2, Database, Printer, Warehouse, PackageX, MapPin, Tag, Hash } from 'lucide-react';
import { printingService, LabelType } from '../../../../services/printingService';
import { LocationSelectionModal } from '../../modules/warehouse-transfer/modals/LocationSelectionModal';
import { warehouseTransferService, ItemLocationInfo } from '../../../../services/warehouseTransferService';
import { stcBinService } from '../../../../services/stcBinService';
import { StcBinDto } from '../../../../types/stcBin';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { ScanTextInput, NumericInput } from '../../../common/inputs';
import { usePrinterFallback } from '../../../../hooks/usePrinterFallback';
import { ReceiptExtraInfo } from './ReceiptExtraInfo';
import { FreeTextFieldsModal, extractFreeTextFields } from '../../../common/FreeTextFieldsModal';

interface ReceivingActiveLineProps {
  line: {
    itemNo: string;
    description1: string;
    quantity: number;           // Bestilt
    totalReceived: number;      // Mottatt
    arrivedQuantity: number;    // Ankommet
    remainingQuantity: number;  // Gjenstår
    itemUnitTxt: string;       // Enhet (stk, kg, etc)
    image?: string;            // Produktbilde URL
    itemType?: number;         // ItemType for batch-validering
  };
  /** Profil-konfigurert tilleggsinfo fra innkjøpsordrelinjen (kun verdier med innhold). Vises under Varenr. */
  extraInfoValues?: string[];
  // For editing existing StcTrans
  editingStcTrans?: {
    itemBatchNo: string;
    itemBatchExpDate: string;
    quantity: number;
    itemType?: number;
    locationName?: string;  // Ankomstlokasjon for eksisterende transaksjon
  } | null;
  onQuantityChange?: (newQuantity: number) => void;
  onBatchInfoChange?: (batchNo: string, expDate: string) => void;
  onSave?: (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => void;
  onCancel?: () => void;
  onDirectReceipt?: (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => void;  // For direktemottak (både nye og eksisterende)
  onDelete?: () => void;          // For sletting (kun i edit-modus)
  currentQuantity?: number;
  showImage?: boolean;
  onOpenGrunndata?: () => void;   // For å åpne grunndata modal
  onPrintLabel?: (reportId: number | null, numLabels: number) => void;  // For å skrive ut etikett
  showPrintSection?: boolean;     // Vis etikett-utskrift seksjon
  pOrderNo?: number;              // Innkjøpsordre-nr (for varemottak — gir POrders/POrderLine i etikett)
  pOrderLineNo?: number;          // Innkjøpsordrelinje-nr (for varemottak)
  companyNo?: string;             // CompanyNo for API calls
  warehouseID?: string;           // WarehouseID for receiving locations
  defaultReceivingLocation?: string;  // Default receiving location from profile (defaultGRBinCode)
  scannedBatchInfo?: {            // Batch-info fra skannet strekkode
    batchNo?: string;
    expDate?: string;
  };
  validationError?: string;       // Red error message for blocking validation
  validationWarning?: string;     // Yellow warning message for non-blocking validation
  enableSSCCPrinting?: boolean;   // Enable SSCC/STAND005 pallet label printing
  companyGS1Prefix?: string;      // Company GS1 prefix for SSCC generation (e.g., "703XXXXX")
  itemGTIN?: string;              // Item's GTIN for SSCC label
  // Sorting box mode
  sortingModeActive?: boolean;
  expectedSortingBox?: { stcBoxTypeID: string; description: string; stcBoxNo: number };
  onCloseSortingBox?: (stcBoxNo: number) => void;
}

export const ReceivingActiveLine: React.FC<ReceivingActiveLineProps> = ({
  line,
  extraInfoValues,
  editingStcTrans,
  onQuantityChange,
  onBatchInfoChange,
  onSave,
  onCancel,
  onDirectReceipt,
  onDelete,
  currentQuantity = 0,
  showImage = false,
  onOpenGrunndata,
  onPrintLabel,
  showPrintSection = false,
  pOrderNo,
  pOrderLineNo,
  companyNo,
  warehouseID,
  defaultReceivingLocation,
  scannedBatchInfo,
  validationError,
  validationWarning,
  enableSSCCPrinting = false,
  companyGS1Prefix,
  itemGTIN,
  sortingModeActive = false,
  expectedSortingBox,
  onCloseSortingBox
}) => {
  const { t } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [quantity, setQuantity] = useState(currentQuantity);
  const [batchNo, setBatchNo] = useState(editingStcTrans?.itemBatchNo || '');
  const [expDate, setExpDate] = useState(editingStcTrans?.itemBatchExpDate || '');
  const [inputJustFocused, setInputJustFocused] = useState(false);
  const [isQuantityTouched, setIsQuantityTouched] = useState(false); // Track if user has interacted with quantity
  const quantityInputRef = useRef<HTMLInputElement>(null);

  // Label printing state
  const [labelTypes, setLabelTypes] = useState<LabelType[]>([]);
  const [selectedLabelType, setSelectedLabelType] = useState<number | null>(null);
  const [numOfLabels, setNumOfLabels] = useState<number>(1);
  const [loadingLabelTypes, setLoadingLabelTypes] = useState(false);

  // Local batch validation error
  const [localBatchError, setLocalBatchError] = useState<string | null>(null);

  // Sorting box confirmation state
  const [sortingBoxConfirmed, setSortingBoxConfirmed] = useState(false);
  const [sortingBoxInput, setSortingBoxInput] = useState('');

  // Saldo modal state
  const [showSaldoModal, setShowSaldoModal] = useState(false);
  const [saldoLocations, setSaldoLocations] = useState<ItemLocationInfo[]>([]);
  const [loadingSaldoLocations, setLoadingSaldoLocations] = useState(false);

  // Receiving location state
  const [receivingLocations, setReceivingLocations] = useState<StcBinDto[]>([]);
  const [selectedReceivingLocation, setSelectedReceivingLocation] = useState<string>(defaultReceivingLocation || '');
  const [loadingReceivingLocations, setLoadingReceivingLocations] = useState(false);

  // SSCC label printing state
  const [ssccPrinting, setSSCCPrinting] = useState(false);
  const [ssccCounter, setSSCCCounter] = useState<number>(() => {
    // Initialize SSCC counter from localStorage for persistence across page reloads
    const stored = localStorage.getItem('ssccCounter');
    return stored ? parseInt(stored, 10) : 1;
  });

  const isDescriptionTruncated = line.description1 && line.description1.length > 60;
  const isEditingMode = !!editingStcTrans;

  // Update batch info, quantity, and receiving location when editingStcTrans changes
  useEffect(() => {
    if (editingStcTrans) {
      console.log('ReceivingActiveLine: editingStcTrans changed:', editingStcTrans);
      setBatchNo(editingStcTrans.itemBatchNo || '');
      setExpDate(editingStcTrans.itemBatchExpDate || '');
      setQuantity(editingStcTrans.quantity || 0);
      // KRITISK: Sett lokasjonen fra eksisterende transaksjon
      if (editingStcTrans.locationName) {
        console.log('ReceivingActiveLine: Setting location from editingStcTrans:', editingStcTrans.locationName);
        setSelectedReceivingLocation(editingStcTrans.locationName);
      } else if (defaultReceivingLocation) {
        // Fallback til default lokasjon hvis ingen lokasjon på transaksjonen
        setSelectedReceivingLocation(defaultReceivingLocation);
      }
    } else {
      // When not editing, check if we have scanned batch info
      if (scannedBatchInfo?.batchNo) {
        console.log('ReceivingActiveLine: Setting batch info from scanned barcode:', scannedBatchInfo);
        setBatchNo(scannedBatchInfo.batchNo);
      } else {
        setBatchNo('');
      }

      if (scannedBatchInfo?.expDate) {
        setExpDate(scannedBatchInfo.expDate);
      } else {
        setExpDate('');
      }
    }
  }, [editingStcTrans, scannedBatchInfo, defaultReceivingLocation]);

  // Only update quantity from currentQuantity when NOT in editing mode
  useEffect(() => {
    if (!editingStcTrans) {
      setQuantity(currentQuantity);
      setIsQuantityTouched(false); // Reset touched state when new line is loaded
    }
  }, [currentQuantity, editingStcTrans]);

  // Load label types when showPrintSection is true
  useEffect(() => {
    if (showPrintSection && companyNo) {
      loadLabelTypes();
    }
  }, [showPrintSection, companyNo]);

  // Load receiving locations when component mounts
  useEffect(() => {
    if (companyNo && warehouseID) {
      loadReceivingLocations();
    }
  }, [companyNo, warehouseID]);

  // Update selected receiving location when default changes
  useEffect(() => {
    if (defaultReceivingLocation && !selectedReceivingLocation) {
      setSelectedReceivingLocation(defaultReceivingLocation);
    }
  }, [defaultReceivingLocation]);

  // Load item's default ReportID when line changes
  useEffect(() => {
    if (showPrintSection && companyNo && line.itemNo) {
      loadItemDefaultReport();
    }
  }, [showPrintSection, companyNo, line.itemNo]);

  // Auto-focus on quantity input when component mounts
  useEffect(() => {
    if (quantityInputRef.current) {
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
    }
  }, []);

  const loadLabelTypes = async () => {
    if (!companyNo) return;

    setLoadingLabelTypes(true);
    try {
      const types = await printingService.getLabelTypesForReceiving(companyNo);
      setLabelTypes(types);

      // Auto-select first label type if none selected
      if (types.length > 0 && !selectedLabelType) {
        setSelectedLabelType(types[0].reportID);
      }
    } catch (error) {
      console.error('Error loading label types:', error);
    } finally {
      setLoadingLabelTypes(false);
    }
  };

  const loadReceivingLocations = async () => {
    if (!companyNo || !warehouseID) return;

    setLoadingReceivingLocations(true);
    try {
      const locations = await stcBinService.getReceivingLocations(companyNo, warehouseID);
      setReceivingLocations(locations);

      // Set default only if nothing is selected yet — preserves location loaded
      // from editingStcTrans (existing receipt line) and any user pick.
      if (!selectedReceivingLocation) {
        if (defaultReceivingLocation && locations.some(loc => loc.binCode === defaultReceivingLocation)) {
          setSelectedReceivingLocation(defaultReceivingLocation);
        } else if (locations.length > 0) {
          setSelectedReceivingLocation(locations[0].binCode);
        }
      }
    } catch (error) {
      console.error('Error loading receiving locations:', error);
    } finally {
      setLoadingReceivingLocations(false);
    }
  };

  const loadItemDefaultReport = async () => {
    if (!companyNo || !line.itemNo) return;

    try {
      const reportId = await printingService.getItemDefaultReport(companyNo, line.itemNo);
      if (reportId && reportId > 0) {
        setSelectedLabelType(reportId);
      }
    } catch (error) {
      console.error('Error loading item default report:', error);
    }
  };

  const printerFallback = usePrinterFallback();

  // Frifelt-modal (FreeText1-5): åpnes før utskrift når valgt mal bruker frifelt
  const [freeTextModalOpen, setFreeTextModalOpen] = useState(false);
  const selectedLabelTypeObj = labelTypes.find((lt) => lt.reportID === selectedLabelType);
  const freeTextFields = extractFreeTextFields(selectedLabelTypeObj?.layoutJson);

  const handlePrintLabel = () => {
    if (!companyNo || !selectedLabelType || numOfLabels <= 0) {
      console.warn('Cannot print: missing companyNo, selectedLabelType, or invalid numOfLabels');
      return;
    }
    // Malen har frifelt → operatøren må fylle inn og bekrefte i frifelt-modalen først
    if (freeTextFields.length > 0) {
      setFreeTextModalOpen(true);
      return;
    }
    void executePrintLabel(undefined);
  };

  /**
   * Kjøres fra frifelt-modalen. Modalen forblir åpen — resultatet returneres og vises
   * der, slik at operatøren kan justere teksten og skrive ut på nytt.
   * `printerIdOverride` er skrivervalget i modalen (null = automatisk oppløsning).
   */
  const handleFreeTextConfirm = async (values: Record<string, string>, printerIdOverride: number | null) => {
    return await executePrintLabel(values, printerIdOverride ?? undefined);
  };

  const executePrintLabel = async (
    freeTextVals?: Record<string, string>,
    printerOverride?: number
  ): Promise<{ success: boolean; message?: string } | undefined> => {
    if (!companyNo || !selectedLabelType) return;

    // Utskrift fra frifelt-modalen: resultatet vises i modalen — ikke som alert
    const fromFreeTextModal = freeTextVals !== undefined;
    let outcome: { success: boolean; message?: string } | undefined;

    const handleGenericResult = (result: any) => {
      outcome = { success: !!result.success, message: result.message };
      if (fromFreeTextModal) return;
      if (result.success) {
        alert(`✓ ${t('warehouse.modules.receipt.activeLine.printSuccess')}\n${result.message || t('warehouse.modules.receipt.activeLine.labelsSentToPrinter', { 0: result.labelsPrinted || numOfLabels })}`);
      } else {
        alert(`✗ ${t('warehouse.modules.receipt.activeLine.printFailed')}\n${result.message}`);
      }
    };

    // Diverse-etiketter (ReportType 60-69) rendres fra custom LabelDesigner-mal uten varedata
    const selectedType = selectedLabelTypeObj?.reportType ?? 0;
    if (selectedType >= 60 && selectedType <= 69) {
      const genericRequest = {
        reportId: selectedLabelType,
        numOfCopies: numOfLabels,
        freeTextValues: freeTextVals,
        printerIdOverride: printerOverride ?? null
      };
      await printerFallback.tryPrint(
        () => printingService.printGenericLabel(companyNo, genericRequest),
        (printerId) => printingService.printGenericLabel(companyNo, { ...genericRequest, printerIdOverride: printerId }),
        handleGenericResult
      );
      return outcome;
    }

    const baseRequest = {
      reportID: selectedLabelType,
      numOfLabels: numOfLabels,
      itemNo: line.itemNo,
      itemUnitCode: line.itemUnitTxt || null,
      batchNo: batchNo || null,
      expiryDate: expDate || null,
      quantity: quantity || null,
      pOrderNo: pOrderNo,
      pOrderLineNo: pOrderLineNo,
      freeTextValues: freeTextVals,
      printerIdOverride: printerOverride ?? null
    };

    console.log('Printing label with request:', baseRequest);

    const handleResult = (result: any) => {
      outcome = { success: !!result.success, message: result.message };
      if (fromFreeTextModal) return;
      if (result.success) {
        console.log('✓ Print successful:', result.message);
        alert(`✓ ${t('warehouse.modules.receipt.activeLine.printSuccess')}\n${result.message || t('warehouse.modules.receipt.activeLine.labelsSentToPrinter', { 0: result.labelsProcessed || numOfLabels })}`);
      } else {
        console.error('Print failed:', result.message);
        alert(`✗ ${t('warehouse.modules.receipt.activeLine.printFailed')}\n${result.message}`);
      }
    };

    await printerFallback.tryPrint(
      () => printingService.printLabel(companyNo, baseRequest),
      (printerId) => printingService.printLabel(companyNo, { ...baseRequest, printerIdOverride: printerId }),
      handleResult
    );
    return outcome;
  };

  /**
   * Handle SSCC/STAND005 pallet label printing
   */
  const handlePrintSSCCLabel = async () => {
    if (!companyNo || !enableSSCCPrinting) {
      console.warn('Cannot print SSCC: missing companyNo or SSCC printing not enabled');
      return;
    }

    setSSCCPrinting(true);
    try {
      // Generate SSCC using company prefix and incrementing counter
      const prefix = companyGS1Prefix || '7030000'; // Default Norwegian GS1 prefix
      const sscc = printingService.generateSSCC(prefix, ssccCounter);

      const ssccRequest = {
        sscc: sscc,
        gtin: itemGTIN || null,
        batchNo: batchNo || null,
        expiryDate: expDate || null,
        quantity: quantity ? Math.floor(quantity) : null,
        itemNo: line.itemNo,
        itemDescription: line.description1,
        numOfCopies: numOfLabels
      };

      console.log('Printing SSCC label with request:', ssccRequest);

      const result = await printingService.printSSCCLabelLegacy(companyNo, ssccRequest);

      if (result.success) {
        // Increment and persist SSCC counter
        const newCounter = ssccCounter + 1;
        setSSCCCounter(newCounter);
        localStorage.setItem('ssccCounter', newCounter.toString());

        console.log('✓ SSCC Print successful:', result.message);
        alert(`✓ ${t('warehouse.modules.receipt.activeLine.ssccPrintSuccess')}\nSSCC: ${sscc}\n${result.message}`);
      } else {
        console.error('SSCC Print failed:', result.message);
        alert(`✗ ${t('warehouse.modules.receipt.activeLine.ssccPrintFailed')}\n${result.message}`);
      }
    } catch (error: any) {
      console.error('Error printing SSCC label:', error);
      alert(`✗ ${t('warehouse.modules.receipt.activeLine.ssccPrintError')}\n${error.message || t('warehouse.modules.receipt.activeLine.unknownError')}`);
    } finally {
      setSSCCPrinting(false);
    }
  };

  const handleOpenSaldoModal = async () => {
    setShowSaldoModal(true);
    setLoadingSaldoLocations(true);
    try {
      const locations = await warehouseTransferService.getLocationsForItem(line.itemNo);
      setSaldoLocations(locations);
    } catch (error) {
      console.error('Error loading locations:', error);
      setSaldoLocations([]);
    } finally {
      setLoadingSaldoLocations(false);
    }
  };

  const handleCloseSaldoModal = () => {
    setShowSaldoModal(false);
    setSaldoLocations([]);
  };

  const handleQuantityChange = (value: number) => {
    console.log('handleQuantityChange called with:', value);
    // When editing existing StcTrans, don't limit to remaining quantity
    const validatedValue = Math.max(0, value);
    console.log('Setting quantity to:', validatedValue);
    setQuantity(validatedValue);
    if (onQuantityChange) {
      onQuantityChange(validatedValue);
    }
  };

  const handleBatchNoChange = (value: string) => {
    setBatchNo(value);
    setLocalBatchError(null);
    if (onBatchInfoChange) {
      onBatchInfoChange(value, expDate);
    }
  };

  const handleExpDateChange = (value: string) => {
    setExpDate(value);
    setLocalBatchError(null);
    if (onBatchInfoChange) {
      onBatchInfoChange(batchNo, value);
    }
  };

  const handleSave = () => {
    if (onSave) {
      const itemType = isEditingMode ? editingStcTrans?.itemType : line.itemType;

      // Validate sorting box confirmation
      if (sortingModeActive && expectedSortingBox && !sortingBoxConfirmed) {
        setLocalBatchError(t('warehouse.modules.receipt.boxIdMismatch', { expected: expectedSortingBox.stcBoxNo }));
        return;
      }

      // Validate batch fields before saving
      // ItemType=1/5: BatchNo required
      if ((itemType === 1 || itemType === 5) && !batchNo?.trim()) {
        setLocalBatchError(t('warehouse.modules.receipt.validation.batchRequired'));
        return;
      }
      // ItemType=4: ExpiryDate required
      if (itemType === 4 && !expDate) {
        setLocalBatchError(t('warehouse.modules.receipt.validation.expiryRequired'));
        return;
      }

      setLocalBatchError(null);
      // Always pass batch info and receiving location (both when editing and creating new)
      onSave(quantity, batchNo || undefined, expDate || undefined, selectedReceivingLocation || undefined);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only save if user has explicitly interacted with quantity field
      // This prevents auto-save when scanner sends Enter after barcode
      if (isQuantityTouched || isEditingMode) {
        handleSave();
      } else {
        console.log('Enter ignored - quantity not touched by user yet');
      }
    } else if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="bg-white border-2 border-blue-300 rounded-lg shadow-md overflow-hidden mb-3 sm:mb-4">
      {/* Header with Title */}
      <div className="bg-blue-500 text-white px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2">
        <Package size={18} className="sm:hidden" />
        <Package size={20} className="hidden sm:block" />
        <h3 className="text-sm sm:text-base font-semibold">
          {isEditingMode ? t('warehouse.modules.receipt.editExistingReceipt') : t('warehouse.modules.receipt.editReceipt')}
        </h3>
      </div>

      {/* Action Buttons Row with Label Settings */}
      <div className="bg-gray-50 px-2 sm:px-3 py-2 flex items-center gap-2 flex-nowrap border-b border-gray-200 overflow-x-auto">
        {/* Grunndata button */}
        {onOpenGrunndata && (
          <button
            onClick={onOpenGrunndata}
            className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors font-medium"
            title={t('warehouse.modules.receipt.maintainMasterData')}
          >
            <Database size={18} />
          </button>
        )}

        {/* Varesaldo button */}
        <button
          onClick={handleOpenSaldoModal}
          className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors font-medium"
          title={t('warehouse.modules.receipt.showItemStock')}
        >
          <Warehouse size={18} />
        </button>

        {/* Avslutt sorteringskasse button - only visible in sorting mode */}
        {sortingModeActive && expectedSortingBox && onCloseSortingBox && (
          <button
            onClick={() => onCloseSortingBox(expectedSortingBox.stcBoxNo)}
            className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors font-medium"
            title={t('warehouse.modules.receipt.finishSortingBox')}
          >
            <PackageX size={18} />
          </button>
        )}

        {/* Print button - item label */}
        <button
          onClick={handlePrintLabel}
          disabled={!selectedLabelType || numOfLabels <= 0}
          className={`flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 rounded transition-colors font-medium ${
            selectedLabelType && numOfLabels > 0
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={t('warehouse.modules.receipt.printItemLabel')}
        >
          <Printer size={18} />
        </button>

        {/* SSCC/STAND005 print button - only shown when enabled */}
        {enableSSCCPrinting && (
          <button
            onClick={handlePrintSSCCLabel}
            disabled={ssccPrinting || numOfLabels <= 0}
            className={`flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 rounded transition-colors font-medium ${
              !ssccPrinting && numOfLabels > 0
                ? 'bg-purple-500 text-white hover:bg-purple-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={t('warehouse.modules.receipt.printSSCCLabel')}
          >
            {ssccPrinting ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <Tag size={18} />
            )}
          </button>
        )}

        {/* Separator */}
        <div className="flex-shrink-0 w-px h-6 bg-gray-300 mx-1"></div>

        {/* Number of labels */}
        <div className="flex-shrink-0 flex items-center gap-1">
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">{t('warehouse.modules.receipt.quantity')}:</label>
          <NumericInput
            mode="integer"
            min="1"
            value={numOfLabels}
            onChange={(e) => setNumOfLabels(parseInt(e.target.value) || 1)}
            className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Label type dropdown */}
        <div className="flex-shrink-0 flex items-center gap-1 min-w-0">
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">{t('warehouse.modules.receipt.label')}:</label>
          <select
            value={selectedLabelType || ''}
            onChange={(e) => setSelectedLabelType(e.target.value ? parseInt(e.target.value) : null)}
            disabled={loadingLabelTypes}
            className="w-32 px-1 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {loadingLabelTypes ? (
              <option value="">{t('common.status.loading')}</option>
            ) : (
              <>
                <option value="">{t('common.select')}</option>
                {labelTypes.map((type) => (
                  <option key={type.reportID} value={type.reportID}>
                    {type.description}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        {/* Separator before receiving location */}
        <div className="flex-shrink-0 w-px h-6 bg-gray-300 mx-1"></div>

        {/* Receiving location dropdown */}
        <div className="flex-shrink-0 flex items-center gap-1 min-w-0">
          <MapPin size={16} className="text-gray-500 flex-shrink-0" />
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">{t('warehouse.modules.receipt.receivingLocation')}:</label>
          <select
            value={selectedReceivingLocation}
            onChange={(e) => setSelectedReceivingLocation(e.target.value)}
            disabled={loadingReceivingLocations}
            className="w-28 px-1 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {loadingReceivingLocations ? (
              <option value="">{t('common.status.loading')}</option>
            ) : receivingLocations.length === 0 ? (
              <option value="">{t('warehouse.modules.receipt.noLocations')}</option>
            ) : (
              <>
                <option value="">{t('warehouse.modules.receipt.selectLocation')}</option>
                {receivingLocations.map((loc) => (
                  <option key={loc.binCode} value={loc.binCode}>
                    {loc.binCode}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-2 sm:p-3">
        {/* Item Info Section - Compact single line */}
        <div className="mb-2">
          <div className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
            <span>
              <span className="font-semibold">{t('common.fields.itemNumber')}:</span> {line.itemNo} - {line.description1}
            </span>
            {line.itemType === 6 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                <Hash size={12} />
                {t('warehouse.modules.receipt.serialNumber')}
              </span>
            )}
            {(line.itemType === 1 || line.itemType === 5) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                {t('warehouse.modules.receipt.activeLine.batchBadge')}
              </span>
            )}
            {line.itemType === 4 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                {t('warehouse.modules.receipt.activeLine.batchWithDateBadge')}
              </span>
            )}
          </div>
          {/* Profil-konfigurert tilleggsinfo fra innkjøpsordrelinjen - egne rader */}
          {extraInfoValues && extraInfoValues.length > 0 && (
            <ReceiptExtraInfo values={extraInfoValues} className="mt-1" />
          )}
        </div>

        {/* Four Column Stats - Compressed */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {/* Bestilt */}
          <div className="bg-gray-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">{t('warehouse.modules.receipt.ordered')}</div>
            <div className="text-base sm:text-lg font-bold text-gray-900">
              {line.quantity} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>

          {/* Mottatt */}
          <div className="bg-green-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">{t('warehouse.modules.receipt.received')}</div>
            <div className="text-base sm:text-lg font-bold text-green-700">
              {line.totalReceived} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>

          {/* Ankommet */}
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">{t('warehouse.modules.receipt.arrived')}</div>
            <div className="text-base sm:text-lg font-bold text-blue-700">
              {line.arrivedQuantity} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>

          {/* Gjenstår */}
          <div className="bg-orange-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">{t('warehouse.modules.receipt.remaining')}</div>
            <div className={`text-base sm:text-lg font-bold ${
              line.remainingQuantity === 0 ? 'text-green-700' : 'text-orange-600'
            }`}>
              {line.remainingQuantity} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>
        </div>

        {/* Batch Info - Single line with label + input for both */}
        {(isEditingMode || line.itemType === 1 || line.itemType === 4 || line.itemType === 5 || scannedBatchInfo?.batchNo || scannedBatchInfo?.expDate) && (
          <div className="flex items-center gap-2 mb-2 flex-nowrap">
            {/* Batch Number - Required for ItemType 1/5, optional for 4 */}
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap flex-shrink-0">
              {t('warehouse.modules.receipt.batchNo')}
              {((isEditingMode && (editingStcTrans?.itemType === 1 || editingStcTrans?.itemType === 5)) ||
                (!isEditingMode && (line.itemType === 1 || line.itemType === 5))) && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <ScanTextInput
              value={batchNo}
              onChange={(e) => handleBatchNoChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`flex-1 min-w-0 px-2 py-1.5 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                ((isEditingMode && (editingStcTrans?.itemType === 1 || editingStcTrans?.itemType === 5) && !batchNo) ||
                 (!isEditingMode && (line.itemType === 1 || line.itemType === 5) && !batchNo))
                  ? 'border-red-300 bg-red-50'
                  : scannedBatchInfo?.batchNo && !isEditingMode
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
              }`}
              placeholder={((isEditingMode && editingStcTrans?.itemType === 4) || (!isEditingMode && line.itemType === 4))
                ? t('warehouse.modules.receipt.activeLine.batchAutoGenerated')
                : t('warehouse.modules.receipt.batchNumber')}
            />

            {/* Expiry Date - Required for ItemType 4, optional for 5 */}
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap flex-shrink-0">
              {t('warehouse.modules.receipt.expiry')}
              {((isEditingMode && editingStcTrans?.itemType === 4) ||
                (!isEditingMode && line.itemType === 4)) && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <input
              type="date"
              value={expDate}
              onChange={(e) => handleExpDateChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-32 flex-shrink-0 px-2 py-1.5 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                ((isEditingMode && editingStcTrans?.itemType === 4 && !expDate) ||
                 (!isEditingMode && line.itemType === 4 && !expDate))
                  ? 'border-red-300 bg-red-50'
                  : scannedBatchInfo?.expDate && !isEditingMode
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
              }`}
            />
          </div>
        )}

        {/* Local Batch Validation Error */}
        {localBatchError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2">
            <p className="text-sm font-medium text-red-700">{localBatchError}</p>
          </div>
        )}

        {/* Validation Messages */}
        {validationError && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-red-700">{validationError}</p>
            </div>
          </div>
        )}

        {validationWarning && !validationError && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mb-4 rounded">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-yellow-700">{validationWarning}</p>
            </div>
          </div>
        )}

        {/* Sorting Box Confirmation */}
        {sortingModeActive && expectedSortingBox && (
          <div className="mb-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
              <div className="text-sm font-medium text-orange-700">
                {t('warehouse.modules.receipt.sortingBoxLabel')}: {expectedSortingBox.description}
              </div>
              <div className="text-lg font-bold text-orange-800">
                #{expectedSortingBox.stcBoxNo}
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.receipt.confirmSortingBox')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={sortingBoxInput}
              onChange={(e) => {
                const val = e.target.value;
                setSortingBoxInput(val);
                setSortingBoxConfirmed(val === String(expectedSortingBox.stcBoxNo));
              }}
              placeholder={t('warehouse.modules.receipt.confirmBoxId')}
              className={`w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:ring-2 ${
                sortingBoxConfirmed
                  ? 'border-green-400 bg-green-50 focus:ring-green-400'
                  : sortingBoxInput.length > 0
                    ? 'border-red-400 bg-red-50 focus:ring-red-400'
                    : 'border-gray-300 focus:ring-blue-400'
              }`}
            />
            {sortingBoxInput.length > 0 && !sortingBoxConfirmed && (
              <p className="text-sm text-red-600 mt-1">
                {t('warehouse.modules.receipt.boxIdMismatch', { expected: expectedSortingBox.stcBoxNo })}
              </p>
            )}
          </div>
        )}

        {/* Quantity Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.modules.receipt.quantityToReceiveNow')}
          </label>
          <div className="flex items-center gap-2">
            <NumericInput
              ref={quantityInputRef}
              mode="decimal"
              value={quantity}
              onChange={(e) => {
                setInputJustFocused(false);
                setIsQuantityTouched(true); // Mark that user has interacted with quantity
                const numValue = parseFloat(e.target.value);
                handleQuantityChange(isNaN(numValue) ? 0 : numValue);
              }}
              onMouseDown={() => setInputJustFocused(true)}
              onFocus={(e) => {
                if (inputJustFocused) {
                  e.target.select();
                }
              }}
              onBlur={() => setInputJustFocused(false)}
              onKeyDown={handleKeyDown}
              className="flex-1 px-4 py-3 text-xl font-bold border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
            <span className="text-base font-medium text-gray-600 min-w-[40px]">
              {line.itemUnitTxt}
            </span>
          </div>
        </div>

        {/* Action Buttons - All on one line */}
        <div className="flex items-center gap-2">
          {/* Lagre - with text */}
          <button
            onClick={handleSave}
            disabled={quantity <= 0 || !!validationError}
            className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded font-medium transition-colors ${
              quantity > 0 && !validationError
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save size={18} />
            <span className="text-sm sm:text-base">{t('common.buttons.save')}</span>
          </button>

          {/* Direktemottak - with text */}
          {onDirectReceipt && (
            <button
              onClick={() => onDirectReceipt(quantity, batchNo || undefined, expDate || undefined, selectedReceivingLocation || undefined)}
              disabled={quantity <= 0 || !!validationError}
              className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded font-medium transition-colors ${
                quantity > 0 && !validationError
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={t('warehouse.modules.receipt.directReceipt.title')}
            >
              <ArrowRight size={18} />
              <span className="text-sm sm:text-base">{t('warehouse.modules.receipt.direct')}</span>
            </button>
          )}

          {/* Slett - icon only */}
          {isEditingMode && onDelete && (
            <button
              onClick={onDelete}
              className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              title={t('common.buttons.delete')}
            >
              <Trash2 size={18} />
            </button>
          )}

          {/* Avbryt - icon only */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              title={t('common.buttons.cancel')}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Saldo Modal */}
      <LocationSelectionModal
        isOpen={showSaldoModal}
        onClose={handleCloseSaldoModal}
        onSelect={() => {}} // Dummy function - vil ikke bli kalt i viewMode
        itemNo={line.itemNo}
        itemDescription={line.description1}
        locations={saldoLocations}
        loading={loadingSaldoLocations}
        viewMode={true}
      />

      {/* Fallback-modal når ingen fast skriver er konfigurert */}
      {printerFallback.modal}

      {/* Frifelt-modal: operatør fyller inn FreeText1-5 med live forhåndsvisning før utskrift */}
      {companyNo && selectedLabelTypeObj?.layoutJson && (
        <FreeTextFieldsModal
          isOpen={freeTextModalOpen}
          companyNo={companyNo}
          layoutJson={selectedLabelTypeObj.layoutJson}
          fields={freeTextFields}
          reportDescription={selectedLabelTypeObj.description}
          context={{ itemNo: line.itemNo, pOrderNo: pOrderNo || undefined, pOrderLineNo: pOrderLineNo || undefined }}
          onCancel={() => setFreeTextModalOpen(false)}
          onConfirm={handleFreeTextConfirm}
        />
      )}
    </div>
  );
};
